import React, { useEffect, useState } from 'react'
import { useGetCurrentUserQuery, useUpdateUserDataMutation, useUserProfileUrlMutation } from '../../../../store/user/userApi'
import { useAppDispatch } from '../../../../app/hooks'
import { tryToUploadChatFiles } from '../../../../store/brokers/partners/chats/ChatsSlice';
import { IToast } from '../../../../interfaces/components/IToast';
import Toast from '../../../../shared/toast';
import ResetPasswordModal from '../modals/ResetPasswordModal';

const Account = () => {
    const dispatch = useAppDispatch();
    const { data: userData, refetch: refetchUser } = useGetCurrentUserQuery()
    const [userProfileUrl, { isLoading: isUploadImageLoading }] = useUserProfileUrlMutation()
    const [updateUserData, { isLoading: isUpdateDataLoading }] = useUpdateUserDataMutation()
    const [showResetPassword, setShowResetPassword] = useState<boolean>(false)
    const [showToast, setShowToast] = useState<IToast | null>();
    const [fullName, setFullName] = useState(userData?.data?.name)
    const [email, setEmail] = useState(userData?.data?.email)
    const [username, setUsername] = useState(userData?.data?.username)
    const [role, setRole] = useState(userData?.data?.type)
    const [avatarUrl, setAvatarUrl] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        setEmail(userData?.data?.email)
        setFullName(userData?.data?.name)
        setUsername(userData?.data?.username)
        setRole(userData?.data?.type)
        setAvatarUrl(userData?.data?.profileUrl)
    }, [userData])

    const saveProfile = async (payload: any) => {
        try {
            await userProfileUrl(payload).unwrap()
            refetchUser()
        } catch (err) {
            console.error(err)
        }
    }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || isUploadImageLoading) return;

        e.target.value = '';

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('files', file);
            const response: any = await dispatch(tryToUploadChatFiles(formData)).unwrap();
            const url = response?.[0]?.url;
            if (url) {
                setAvatarUrl(url);
                await saveProfile({ url });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsUploading(false);
        }
    };


    const handleEdit = () => {
        if (isEditing) return
        setIsEditing(true)
    }

    const saveData = async (data: any) => {
        if (isUpdateDataLoading) return;
        try {
            await updateUserData(data).unwrap()
        } catch (err) {
            console.error(err)
        }
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            await saveData({ name: fullName, email })
            setIsEditing(false)
        } catch (err) {
            console.error(err)
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        setIsEditing(false)
        setFullName('')
        setEmail('')
    }

    const onShowMessage = (type: string, message: string) => {
        setShowToast({ type, message });
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';
        return text
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    return (
        <div className='min-h-screen w-full bg-white px-4 py-10 text-gray-900 dark:bg-black dark:text-gray-50 sm:px-8'>
            <div className='mx-auto w-full max-w-6xl'>
                <section className='flex flex-col gap-6'>
                    <h1 className='text-xl tracking-tight text-gray-900 dark:text-gray-50'>Profile Photo</h1>
                    <div className='flex flex-col gap-6 sm:flex-row sm:items-center'>
                        <div className='flex items-center justify-center rounded-full dark:text-emerald-300'>
                            {avatarUrl ? (
                                <img
                                    className='w-12 h-12 sm:w-16 sm:h-16 bg-black dark:bg-white rounded-full mr-2'
                                    src={avatarUrl || '/assets/layout/profile-icon.svg'}
                                    alt='main photo'
                                />
                            ) : (
                                <img src={'/assets/layout/profile-icon.svg'} className='w-[60px] object-contain' />
                            )}
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label htmlFor='avatar-upload' className='inline-flex cursor-pointer items-center justify-center rounded-lg bg-[#00330F] px-6 py-2 text-base font-medium text-white transition hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:bg-emerald-800 dark:hover:bg-emerald-700'>
                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' className='mr-2 h-5 w-5' fill='none' stroke='currentColor' strokeWidth='1.6'>
                                    <path d='M12 16V5.5' strokeLinecap='round' strokeLinejoin='round' />
                                    <path d='M9 8.5 12 5.5 15 8.5' strokeLinecap='round' strokeLinejoin='round' />
                                    <path d='M5 18.5h14' strokeLinecap='round' strokeLinejoin='round' />
                                </svg>
                                {isUploading ? 'Uploading...' : 'Upload Photo'}
                            </label>
                            <input id='avatar-upload' type='file' accept='image/*' className='hidden' onChange={handleImageUpload} />
                            <p className='text-sm text-gray-500 dark:text-gray-400'>JPG, PNG or GIF. Max size 2MB</p>
                        </div>
                    </div>
                </section>

                <section className='mt-12 flex flex-col gap-6'>
                    <h2 className='text-xl tracking-tight text-gray-900 dark:text-gray-50'>Account Information</h2>
                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                        <div className='flex flex-col gap-2'>
                            <span className='text-sm font-semibold text-gray-800 dark:text-gray-200'>Full Name</span>
                            <input type='text' value={fullName} onChange={e => setFullName(e.target.value)} disabled={!isEditing} className={`w-full rounded-xl px-4 py-3 text-base outline-none transition ${isEditing ? 'border border-gray-300 bg-white text-gray-900 focus:border-emerald-900 focus:ring-1 focus:ring-emerald-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-50' : 'border border-transparent bg-gray-100 text-gray-900 dark:bg-bgColor-darkMode dark:text-gray-100'}`} />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <span className='text-sm font-semibold text-gray-800 dark:text-gray-200'>Email address</span>
                            <input type='email' value={email} onChange={e => setEmail(e.target.value)} disabled={!isEditing} className={`w-full rounded-xl px-4 py-3 text-base outline-none transition ${isEditing ? 'border border-gray-300 bg-white text-gray-900 focus:border-emerald-900 focus:ring-1 focus:ring-emerald-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-50' : 'border border-transparent bg-gray-100 text-gray-900 dark:bg-bgColor-darkMode dark:text-gray-100'}`} />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <span className='text-sm font-semibold text-gray-800 dark:text-gray-200'>Username</span>
                            <input type='text' value={username} disabled className='w-full cursor-not-allowed rounded-xl border border-transparent bg-gray-100 px-4 py-3 text-base text-gray-800 outline-none dark:bg-bgColor-darkMode dark:text-gray-200' />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <span className='text-sm font-semibold text-gray-800 dark:text-gray-200'>Role</span>
                            <input type='text' value={formatText(role)} disabled className='w-full cursor-not-allowed rounded-xl border border-transparent bg-gray-100 px-4 py-3 text-base text-gray-800 outline-none dark:bg-bgColor-darkMode dark:text-gray-200' />
                        </div>
                    </div>

                    <div className='mt-4 flex flex-wrap gap-4'>
                        {isEditing ? (
                            <>
                                <button onClick={handleSave} disabled={isSaving} className='inline-flex items-center justify-center rounded-lg bg-[#00330F] px-6 py-3 text-base font-medium text-white transition hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-emerald-800 dark:hover:bg-emerald-700'>
                                    {isSaving ? 'Saving...' : 'Save changes'}
                                </button>
                                <button onClick={handleCancel} className='inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-800 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'>
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={handleEdit} className='inline-flex items-center justify-center rounded-lg bg-[#00330F] px-6 py-3 text-base font-medium text-white transition hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-700  dark:bg-emerald-800 dark:hover:bg-emerald-700'>
                                    Edit Account Information
                                </button>
                                <button onClick={() => setShowResetPassword(true)} className='inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-800 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:border-gray-600 dark:bg-bgColor-darkMode dark:text-gray-100 dark:hover:bg-gray-700'>
                                    Reset password
                                </button>
                            </>
                        )}
                    </div>
                </section>
            </div>
            {showResetPassword &&
                <ResetPasswordModal
                    openModal={showResetPassword}
                    handleCloseModal={() => setShowResetPassword(false)}
                    onShowMessage={onShowMessage}

                />
            }
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
        </div>
    )
}

export default Account
